import React, {useState, useRef, useCallback} from 'react';
import {Center, Flex, Image, Spinner, useTheme, Stack, Link} from '@chakra-ui/react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import Header from './Header';
import BackButton from '../../components/BackButton';
import PlaceTabs from './Tabs';
import useGetUserLocation, {Location} from '../../hooks/useUserLocation';
import LocationDisabled from '../../components/LocationDisabled';
import {useOffers} from '../../hooks/useOffers';
import {useFetchPlace} from '../../hooks/useFetchPlace';
import MediaGallery from '../../components/MediaGallery';
import {logPlaceViewItem} from '../../utils/analytics';
import PartnerCard from '../../components/Articles/PartnerCard';
import {H2} from '../../components/typography';
import MediaEditor from "../../components/MediaEditor";
import useCheckIn from '../../hooks/useCheckIn';
import {OfferType} from '../../types/article';
import DefaultPlaceholderImage from '../../assets/images/place_default.webp';

const Place = ({
  assignPlaceName,
  onClose,
}: {
  assignPlaceName?: (string) => void;
  onClose?: () => void;
}) => {
  const {placeId} = useParams();
  const theme = useTheme();
  const userLocation = useGetUserLocation();
  const navigate = useNavigate();
  const [verifiedLocation, setVerifiedLocation] = useState<Location | null>(null);
  const [locationDisabled, setLocationDisabled] = useState(false);
  const [searchParams] = useSearchParams();
  const viewItemTracked = useRef(false);

  const { data, isLoading, refetch } = useFetchPlace({
    id: Number(placeId || searchParams.get('id')),
    lat: userLocation?.data?.latitude,
    long: userLocation?.data?.longitude,
  });
  const place = data?.data;

  const {offers, offersIsLoading} = useOffers({
    articleType: 'placeId',
    articleId: placeId || searchParams.get('id'),
  });

  const {placeCheckIn} = useCheckIn();

  const navigateBack = React.useCallback(() => {
    if (searchParams.has('id') && onClose) {
      onClose();
      return;
    }
    navigate(-1);
  }, []);

  // Bypass location check and proceed with check-in using sdv (skip distance verification)
  const handleBypassLocationCheckIn = useCallback(() => {
    if (!place) return;

    const boostedCheckin = offers.filter((offer) => offer.offer_type === OfferType.POINTS_BOOST);
    let offerId;
    if (boostedCheckin.length > 0) {
      offerId = boostedCheckin[0].id;
    }

    placeCheckIn({
      place: place,
      latitude: undefined,
      longitude: undefined,
      isPublic: true,
      offerId: offerId,
      offers: offers,
      sdv: true,
      qr_code_id: undefined,
    });
    setLocationDisabled(false);
  }, [place, offers, placeCheckIn]);

  React.useEffect(() => {
    if (!verifiedLocation && userLocation.status === 'success') {
      setVerifiedLocation(userLocation.data);
    }
  }, [userLocation.status]);

  // Track place view analytics when place data is loaded
  React.useEffect(() => {
    if (place?.id && place?.name && !viewItemTracked.current) {
      logPlaceViewItem(place);
      viewItemTracked.current = true;
    }
  }, [place?.id, place?.name]);

  if (!verifiedLocation && locationDisabled) return <LocationDisabled onBypassLocation={handleBypassLocationCheckIn} entityName={place?.name} />;

  if (offersIsLoading || isLoading || place === undefined) {
    return (
      <Center style={centerCss}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );
  }

  return (
    <>
      <Flex position="relative" height="275px">
        <BackButton onClick={navigateBack} m={'.75rem'} pos="relative" />
        {/* <Image
          fallbackSrc={FallbackImg}
          src={place.image_url}
          alt="cover image"
          position="absolute"
          width="100%"
          height="100%"
          objectFit="cover"
          zIndex="-1"
          borderBottomRadius="14px"
        /> */}
        <MediaGallery media={place.media} placeholderSrc={DefaultPlaceholderImage} />
        <MediaEditor
          media={place.media}
          entityType="places"
          entityId={place.id}
          refetch={refetch}
        />
      </Flex>
      <Header place={place} refetchPlace={refetch} />
      <PlaceTabs
        place={place}
        placeOffers={offers}
        refetchPlace={refetch}
        disableLocation={() => setLocationDisabled(true)}
      />
      {place.partner && (
        <Stack gap="0rem" p="1rem" pb="50px" pt={0} textAlign="left">
          <H2 my={0}>Partner</H2>
          <PartnerCard partner={place.partner} />
        </Stack>
      )}
      {/* <PlaceAbout place={place} /> */}
    </>
  );
};

export default Place;

const centerCss: React.CSSProperties = {
  minHeight: '100vh',
};
